import * as React from "react";
import { cn } from "../../lib/utils";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead 
    ref={ref} 
    className={cn(
      "[&_tr]:border-b bg-muted/30",
      className
    )} 
    {...props} 
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    /** Make row interactive/clickable */
    interactive?: boolean;
  }
>(({ className, interactive, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors data-[state=selected]:bg-muted",
      // Ultra-subtle hover - just enough to show interactivity
      "hover:bg-muted/40",
      // Interactive rows get cursor and slightly more emphasis
      interactive && "cursor-pointer hover:bg-muted/60 active:bg-muted/80",
      className
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    /** Visually emphasize this column as primary */
    primary?: boolean;
    /** Make column sortable (shows hover state) */
    sortable?: boolean;
  }
>(({ className, primary, sortable, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-4 text-left align-middle text-xs font-medium uppercase tracking-wider",
      "text-muted-foreground [&:has([role=checkbox])]:pr-0",
      // Primary column - slightly more prominent
      primary && "text-foreground font-semibold",
      // Sortable columns get hover state
      sortable && "cursor-pointer hover:text-foreground transition-colors select-none",
      className
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    /** Visually emphasize this cell (e.g., primary identifier) */
    primary?: boolean;
    /** Mute this cell (secondary information) */
    muted?: boolean;
  }
>(({ className, primary, muted, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-4 align-middle [&:has([role=checkbox])]:pr-0",
      // Primary cells - the main identifier column
      primary && "font-medium text-foreground",
      // Muted cells - less important info
      muted && "text-muted-foreground text-sm",
      className
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

/** Optional caption for accessibility */
const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
};
